/**
 * Enterprise Lesson Runner — breadcrumb bar, instruction panel, completion overlay.
 * Renders inside #sim-container during enterprise lessons.
 */
import type { EnterpriseStep, EnterpriseLessonDef } from '@graphicsim/shared-types';
import { navigate } from '../../router';
import { getEnterpriseLesson } from '../../data/enterprise-lessons';
import {
  startEnterpriseLesson,
  stopEngine,
  setOnStepActivated,
  setOnLessonComplete,
  setOnActionReady,
  advanceInfoStep,
} from '../../engine/enterpriseLessonEngine';
import { playStepAudio, stopAudio } from '../../engine/enterpriseAudio';
import { closeVideoBoard } from '../../ui/program';
import { openLessonVideoModal, destroyLessonVideoModal, reopenLessonVideoModal } from '../../ui/lessonVideoModal';
import { renderLessonChecklist, destroyLessonChecklist } from '../../ui/lessonChecklist';
import { startBugReporter, stopBugReporter, reportUserIssue } from '../../engine/bugReporter';

let breadcrumbBar: HTMLElement | null = null;
let instructionPanel: HTMLElement | null = null;
let completionOverlay: HTMLElement | null = null;
let activeLessonDef: EnterpriseLessonDef | null = null;

export function renderEnterpriseLessonRunner(lessonId: string): void {
  const lessonDef = getEnterpriseLesson(lessonId);
  if (!lessonDef) {
    console.error(`Enterprise lesson not found: ${lessonId}`);
    navigate('/lessons');
    return;
  }

  activeLessonDef = lessonDef;

  // Begin auto-capture of runtime errors for this lesson
  startBugReporter(lessonDef.id);

  // Close any existing video board tab (user opens it manually during the lesson)
  closeVideoBoard();

  // Create breadcrumb bar (with tutorial link when applicable)
  createBreadcrumbBar(lessonDef);

  setOnLessonComplete(() => {
    showCompletionOverlay(lessonDef);
  });

  if (lessonDef.mode === 'checklist') {
    // Checklist lessons: right-side panel + optional tutorial video modal.
    // No instruction panel, no lockdown — sim is fully usable.
    if (lessonDef.tutorialVideoUrl) {
      openLessonVideoModal(lessonDef.tutorialVideoUrl, 'Tutorial', true);
    }
    renderLessonChecklist(lessonDef.tasks ?? [], {
      hasTutorialVideo: !!lessonDef.tutorialVideoUrl,
    });
    startEnterpriseLesson(lessonDef);
    return;
  }

  // Step-mode lessons (L1 format)
  createInstructionPanel();

  setOnStepActivated((_sectionIndex, _stepIndex, step) => {
    updateInstruction(step);
  });

  setOnActionReady(() => {
    // Trigger threshold satisfied on a requireNextClick action — enable Next.
    if (!instructionPanel) return;
    const nextBtn = instructionPanel.querySelector<HTMLButtonElement>('.ent-instr-next');
    if (nextBtn) {
      nextBtn.disabled = false;
      nextBtn.classList.remove('disabled');
    }
  });

  startEnterpriseLesson(lessonDef);
}

export function removeEnterpriseLessonRunner(): void {
  stopEngine();
  stopAudio();
  destroyLessonChecklist();
  destroyLessonVideoModal();
  stopBugReporter();
  destroyReportModal();
  activeLessonDef = null;

  if (breadcrumbBar) {
    breadcrumbBar.remove();
    breadcrumbBar = null;
  }
  if (instructionPanel) {
    instructionPanel.remove();
    instructionPanel = null;
  }
  if (completionOverlay) {
    completionOverlay.remove();
    completionOverlay = null;
  }
}

// ── Breadcrumb Bar ──────────────────────────────────────────

function createBreadcrumbBar(lessonDef: EnterpriseLessonDef): void {
  if (breadcrumbBar) breadcrumbBar.remove();

  breadcrumbBar = document.createElement('div');
  breadcrumbBar.className = 'ent-breadcrumb-bar';

  const exitBtn = document.createElement('button');
  exitBtn.className = 'ent-breadcrumb-exit';
  exitBtn.textContent = 'Exit';
  exitBtn.title = 'Back to Lessons';
  exitBtn.addEventListener('click', () => {
    removeEnterpriseLessonRunner();
    navigate('/lessons');
  });

  const lessonName = document.createElement('span');
  lessonName.className = 'ent-breadcrumb-name';
  lessonName.textContent = lessonDef.name;

  breadcrumbBar.appendChild(exitBtn);
  breadcrumbBar.appendChild(lessonName);

  if (lessonDef.tutorialVideoUrl) {
    const videoLink = document.createElement('button');
    videoLink.className = 'ent-breadcrumb-video-link';
    videoLink.textContent = 'Tutorial Video';
    videoLink.addEventListener('click', () => reopenLessonVideoModal());
    breadcrumbBar.appendChild(videoLink);
  }

  const reportBtn = document.createElement('button');
  reportBtn.className = 'ent-breadcrumb-report';
  reportBtn.textContent = 'Report Issue';
  reportBtn.title = 'Report a bug or send feedback about this lesson';
  reportBtn.addEventListener('click', () => openReportModal(lessonDef));
  breadcrumbBar.appendChild(reportBtn);

  const simContainer = document.getElementById('sim-container');
  if (simContainer) {
    simContainer.insertBefore(breadcrumbBar, simContainer.firstChild);
  }
}

// ── Report Issue Modal ──────────────────────────────────────

let reportModal: HTMLElement | null = null;

function openReportModal(lessonDef: EnterpriseLessonDef): void {
  if (reportModal) reportModal.remove();

  reportModal = document.createElement('div');
  reportModal.className = 'ent-report-modal';
  reportModal.innerHTML = `
    <div class="ent-report-backdrop"></div>
    <div class="ent-report-card" role="dialog" aria-label="Report Issue">
      <div class="ent-report-header">
        <span class="ent-report-title">Report an Issue</span>
        <button class="ent-report-close" aria-label="Close">&times;</button>
      </div>
      <p class="ent-report-context">Lesson: <strong>${lessonDef.name}</strong></p>
      <textarea class="ent-report-text" rows="6" placeholder="What went wrong? What were you trying to do?"></textarea>
      <div class="ent-report-actions">
        <button class="ent-report-cancel">Cancel</button>
        <button class="ent-report-submit">Send Report</button>
      </div>
      <div class="ent-report-toast" style="display:none;">Thanks — report sent.</div>
    </div>
  `;
  document.body.appendChild(reportModal);

  const textarea = reportModal.querySelector<HTMLTextAreaElement>('.ent-report-text')!;
  const submit = reportModal.querySelector<HTMLButtonElement>('.ent-report-submit')!;
  const toast = reportModal.querySelector<HTMLElement>('.ent-report-toast')!;

  const close = () => destroyReportModal();
  reportModal.querySelector('.ent-report-close')?.addEventListener('click', close);
  reportModal.querySelector('.ent-report-cancel')?.addEventListener('click', close);
  reportModal.querySelector('.ent-report-backdrop')?.addEventListener('click', close);

  submit.addEventListener('click', async () => {
    const text = textarea.value.trim();
    if (!text) {
      textarea.focus();
      return;
    }
    submit.disabled = true;
    await reportUserIssue(text, lessonDef.id);
    toast.style.display = '';
    setTimeout(close, 1200);
  });

  setTimeout(() => textarea.focus(), 0);
}

function destroyReportModal(): void {
  if (reportModal) {
    reportModal.remove();
    reportModal = null;
  }
}

// ── Instruction Panel ───────────────────────────────────────

function createInstructionPanel(): void {
  if (instructionPanel) instructionPanel.remove();

  instructionPanel = document.createElement('div');
  instructionPanel.className = 'ent-instruction-panel';
  instructionPanel.innerHTML = `
    <div class="ent-instr-header">
      <span class="ent-instr-title"></span>
    </div>
    <p class="ent-instr-text"></p>
    <div class="ent-instr-actions">
      <button class="ent-instr-next" style="display:none;">Next &rarr;</button>
    </div>
  `;

  // Wire Next button
  instructionPanel.querySelector('.ent-instr-next')?.addEventListener('click', () => {
    advanceInfoStep();
  });

  document.body.appendChild(instructionPanel);
}

function updateInstruction(step: EnterpriseStep): void {
  if (!instructionPanel) return;

  const title = instructionPanel.querySelector('.ent-instr-title')!;
  const text = instructionPanel.querySelector('.ent-instr-text')!;
  const nextBtn = instructionPanel.querySelector<HTMLButtonElement>('.ent-instr-next')!;

  title.textContent = step.title;
  text.textContent = step.instruction;

  // Show Next button for info steps and for action steps that require an explicit Next click;
  // for plain action steps the trigger advances the lesson directly.
  if (step.type === 'info') {
    nextBtn.style.display = '';
    // Disable Next until audio finishes (if audio exists)
    if (step.audioUrl) {
      nextBtn.disabled = true;
      nextBtn.classList.add('disabled');
    } else {
      nextBtn.disabled = false;
      nextBtn.classList.remove('disabled');
    }
  } else if (step.requireNextClick) {
    // Visible but disabled until the trigger threshold is met (onActionReady fires).
    nextBtn.style.display = '';
    nextBtn.disabled = true;
    nextBtn.classList.add('disabled');
  } else {
    nextBtn.style.display = 'none';
  }

  // Auto-play audio — enable Next when it finishes
  playStepAudio(step.audioUrl, () => {
    if (!instructionPanel) return;
    nextBtn.disabled = false;
    nextBtn.classList.remove('disabled');
  });
}

// ── Completion Overlay ──────────────────────────────────────

function showCompletionOverlay(lessonDef: EnterpriseLessonDef): void {
  // Remove instruction panel and breadcrumb
  if (instructionPanel) {
    instructionPanel.remove();
    instructionPanel = null;
  }

  completionOverlay = document.createElement('div');
  completionOverlay.className = 'ent-completion-overlay';
  completionOverlay.innerHTML = `
    <div class="ent-completion-card">
      <div class="ent-completion-check">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="#2a9f45">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      </div>
      <h2 class="ent-completion-heading">Lesson Complete!</h2>
      <p class="ent-completion-name">${lessonDef.name}</p>
      <button class="ent-btn ent-completion-btn">Back to Lessons</button>
    </div>
  `;

  completionOverlay.querySelector('.ent-completion-btn')?.addEventListener('click', () => {
    removeEnterpriseLessonRunner();
    navigate('/lessons');
  });

  document.getElementById('sim-container')?.appendChild(completionOverlay);
}

